import { EActionState } from '@/models';
import {
	IScheduledAction,
	IScheduledActionGroup,
	ITriggerRequest
} from '@/models/schedule';
import { ITrackedScheduledActionState } from '@/store/schedule/actions';

const clearTracked = (
	selected: IScheduledAction[],
	tracked: ITrackedScheduledActionState[],
	key?: string
) => {
	if (key) {
		const selectedIndex = selected.findIndex(a => a.key === key);
		const trackedIndex = tracked.findIndex(a => a.data.key === key);

		if (selectedIndex !== -1) {
			selected.splice(selectedIndex, 1);
		}
		if (trackedIndex !== -1) {
			tracked.splice(trackedIndex, 1);
		}
	} else {
		tracked.forEach(a => {
			const index = selected.findIndex(sa => sa.key === a.data.key);
			if (index !== -1) {
				selected.splice(index, 1);
			}
		});
		tracked.splice(0, tracked.length);
	}
};

const createTriggerRequest = (
	actions: IScheduledAction[]
): ITriggerRequest[] => {
	const request: ITriggerRequest[] = actions.map(value => {
		return { actionId: value.actionId, appointmentId: value.appointmentId };
	});
	return request;
};

const onRefreshActionStart = (
	actions: ITrackedScheduledActionState[],
	key: string
) => {
	const index = actions.findIndex(a => a.data.key === key);
	if (index !== -1) {
		actions[index].loaded = EActionState.RUNNING;
	}
};

const onRefreshActionCompleted = (
	actions: IScheduledActionGroup[],
	selected: IScheduledAction[],
	tracked: ITrackedScheduledActionState[],
	updated: IScheduledAction
) => {
	const groupIndex = actions.findIndex(
		a => a.appointmentId === updated.appointmentId
	);
	if (groupIndex !== -1) {
		const groupActionIndex = actions[groupIndex].actions.findIndex(
			a => a.key === updated.key
		);
		if (groupActionIndex !== -1) {
			actions[groupIndex].actions[groupActionIndex] = updated;
		}
	}

	const selectedIndex = selected.findIndex(a => a.key === updated.key);
	if (selectedIndex !== -1) {
		selected[selectedIndex] = updated;
	}

	const trackedIndex = tracked.findIndex(a => a.data.key === updated.key);
	if (trackedIndex !== -1) {
		tracked[trackedIndex].data = updated;
		tracked[trackedIndex].loaded = EActionState.COMPLETED;
	}
};

const onRefreshActionFailed = (
	actions: ITrackedScheduledActionState[],
	key: string
) => {
	const index = actions.findIndex(a => a.data.key === key);
	if (index !== -1) {
		actions[index].loaded = EActionState.FAILED;
	}
};

const reconcileActions = (
	selected: IScheduledAction[],
	tracked: ITrackedScheduledActionState[],
	updated: IScheduledActionGroup[]
) => {
	if (selected.length > 0) {
		selected.forEach((a, index) => {
			const group = updated.filter(
				u => u.appointmentId === a.appointmentId
			)[0];
			if (group) {
				const action = group.actions.filter(ga => ga.key === a.key)[0];
				if (action) {
					selected[index] = action;
				}
			}
		});
	}
	if (tracked.length > 0) {
		tracked.forEach(a => {
			const group = updated.filter(
				u => u.appointmentId === a.data.appointmentId
			)[0];
			if (group) {
				const action = group.actions.filter(
					ga => ga.key === a.data.key
				)[0];
				if (action) {
					a.data = action;
				}
			}
		});
	}
};

const setSelected = (
	selected: IScheduledAction[],
	tracked: ITrackedScheduledActionState[],
	action: IScheduledAction,
	checked: boolean
) => {
	const selectedIndex = selected.findIndex(a => a.key === action.key);
	const trackedIndex = tracked.findIndex(a => a.data.key === action.key);

	if (checked) {
		if (selectedIndex === -1) {
			selected.push(action);
		}
		if (trackedIndex === -1) {
			tracked.push({
				data: action,
				loaded: EActionState.PENDING
			});
		}
	} else {
		if (selectedIndex !== -1) {
			selected.splice(selectedIndex, 1);
		}
		if (trackedIndex !== -1) {
			tracked.splice(trackedIndex, 1);
		}
	}
};

export const scheduleService = {
	clearTracked,
	createTriggerRequest,
	onRefreshActionStart,
	onRefreshActionCompleted,
	onRefreshActionFailed,
	reconcileActions,
	setSelected
};
